import { HttpClient } from '@angular/common/http';
import { 
  computed, 
  DestroyRef,
  inject, 
  Injectable, 
  signal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { environment } from '@environments/environment';

import { 
  forkJoin, 
  map,
  Observable, 
  switchMap, 
  tap,
  catchError
} from 'rxjs';

import { PubsubService } from '@core/pubsub/services/pubsub.service';
import { 
  Notification, 
  NotificationResponse, 
  NotificationsPayload 
} from '../models';
import { ResponseData, StatusDataEnum } from '@shared/helpers';

@Injectable({
  providedIn: 'root'
})
export class NotificationsService {
  #http = inject(HttpClient);
  #destroyRef = inject(DestroyRef);
  #pubsubService = inject(PubsubService);
  #urlBase = `${environment.urlProfileManagement}/api/v1`;

  //Service Center Tasks
  readonly #openTasksCountSignal = signal<number>(0);
  public readonly openTasksCount = this.#openTasksCountSignal.asReadonly();

  readonly #headerNotificationsSignal = signal<Notification[]>([]);
  public readonly headerNotifications = this.#headerNotificationsSignal.asReadonly();
  public readonly unreadNotificationsCount = signal<number>(0);

  readonly #pageNotificationsSignal = signal<ResponseData<NotificationResponse>>({ 
    data: {
      count: 0,
      today: [],
      lastWeek: [],
      lastMonth: [],
      thisMonth: []
    },
    status: StatusDataEnum.LOADING
  });
  public readonly pageNotifications = this.#pageNotificationsSignal.asReadonly();

  constructor() {
    this.#pubsubService.taskEvent$
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((event: Notification) => this.#handlePubsubTaskEvent(event));

    this.#pubsubService.notificationEvent$
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: (event: Notification) => {
          this.#handlePubsubNotificationEvent(event);
        },
        error: (error) => {
          console.error('❌ [NotificationService] Error in notification subscription:', error);
        },
      });
  }

/* --------------------------------- Socket Connection -------------------------------- */

  public initialize(userId: number): void {
    this.#pubsubService
      .connect(userId)
      .pipe(
        switchMap(() => {
          return forkJoin({
            // tasks: this.#getServiceCenterTasks(userId)
            notifications: this.getHeaderNotifications({ 
              userId: userId,
              limit: 5
            })
          });
        })
      )
      .subscribe({
        error: (err) => console.error('❌ [NotificationService] Initialization failed:', err)
      });
  }

  public disconnect(): void {
    this.#headerNotificationsSignal.set([]);
    this.#pageNotificationsSignal.set({ 
      data: {
        count: 0,
        today: [],
        lastWeek: [],
        lastMonth: [],
        thisMonth: []
      },
      status: StatusDataEnum.ERROR
    });
    this.#pubsubService.disconnect();
  }

/* --------------------------------- Get Notifications ----------------------------- */

  /**
   * Load notifications for header (max 5, unread only)
   * Transforms grouped response into a single array for header display
   * @param params Notifications payload with type and source system 
   * @returns Observable of notifications array
   */
  public getHeaderNotifications(params: NotificationsPayload): Observable<Notification[]> {
    const headerParams = { ...params, limit: 5 };
    return this.#http
      .post<NotificationResponse>(`${this.#urlBase}/notifications`, headerParams)
      .pipe(
        map((response: NotificationResponse) => {
          this.unreadNotificationsCount.set(response.count);
          const allNotifications: Notification[] = [
            ...(response.today || []),
            ...(response.lastWeek || []),
            ...(response.lastMonth || []),
            ...(response.thisMonth || []),
          ];
          return allNotifications;
        }),
        tap((notifications) => this.#headerNotificationsSignal.set(notifications))
      );
  }

  /**
   * Load notifications for page listing (all notifications)
   * Updates the ResponseData signal with loading/success/error states
   * @param params Notifications payload with type and source system 
   * @returns Observable of notification response with groups
   */
  public getPageNotifications(params: NotificationsPayload): Observable<NotificationResponse> {
    this.#pageNotificationsSignal.update(current => ({
      ...current,
      status: StatusDataEnum.LOADING
    }));

    return this.#http
      .post<NotificationResponse>(`${this.#urlBase}/notifications`, params)
      .pipe(
        tap((response) => {
          this.#pageNotificationsSignal.set({
            data: response,
            status: StatusDataEnum.SUCCESS
          });
          this.unreadNotificationsCount.set(response.count);
        }),
        catchError((error) => {
          this.#pageNotificationsSignal.update(current => ({
            ...current,
            status: StatusDataEnum.ERROR
          }));
          throw error;
        })
      );
  }

  /**
   * Service center tasks summary
   * @param userId 
   * @returns 
   */
  #getServiceCenterTasks(userId: number): Observable<{openTasks: number}> {
    return this.#http
      .get<{openTasks: number}>(`${this.#urlBase}/notifications/service-center/summary/${userId}`)
      .pipe(tap((summary) => this.#openTasksCountSignal.set(summary.openTasks)));
  }

/* --------------------------------- Handle Pubsub Events Functions ------------------- */

  #handlePubsubTaskEvent(event: Notification): void {
    this.#openTasksCountSignal.set(event.count as number);
  }

  #handlePubsubNotificationEvent(event: Notification): void {
    if (this.#notificationExists(event.notificationRecipientsId)) {
      return;
    }
    
    const newNotification: Notification = {
      notificationRecipientsId: event.notificationRecipientsId,
      title: event.title,
      message: event.message,
      isRead: event.isRead,
      creationDate: event.creationDate,
      documentSource: event.documentSource,
      sourceSystem: event.sourceSystem,
      path: event.path
    };

    this.addNewNotification(newNotification);
  }

  /**
   * Check if notification already exists in header or page notifications
   * @param notificationId The notification recipient ID to check
   * @returns boolean indicating if notification already exists
   */
  #notificationExists(notificationId: string): boolean {
    const headerExists = this.#headerNotificationsSignal()
      .some(n => n.notificationRecipientsId === notificationId);
    
    if (headerExists) return true;
    
    const pageData = this.#pageNotificationsSignal().data;
    if (!pageData) return false;
    
    const pageExists = [
      ...(pageData.today || []),
      ...(pageData.lastWeek || []),
      ...(pageData.lastMonth || []),
      ...(pageData.thisMonth || []),
    ].some(n => n.notificationRecipientsId === notificationId);
    
    return pageExists;
  }

/* ---------------------------------- Notifications --------------------------------- */
  public markAsRead(notificationId: string): Observable<void> {
    return this.#http.post<void>(`${this.#urlBase}/notifications/read`, { 
      notificationRecipientIds: [notificationId]
    }).pipe(
      tap(() => {
        this.unreadNotificationsCount.update(count => count - 1);
        this.updatePageNotificationReadStatus(notificationId)
      })
    );
  }

  public markAllAsRead(userId: number): Observable<void> {
    return this.#http.post<void>(`${this.#urlBase}/notifications/read`, { notificationRecipientIds: [], userId: userId })
      .pipe(
        tap(() => {
          this.#headerNotificationsSignal.set([]);

          //se usuario marcar no header e estiver na lista
          if(this.#pageNotificationsSignal().data) {
            this.getPageNotifications({ userId }).subscribe();
          }
        })
      );
  }

  /**
   * Check if any notification IDs exist in header notifications
   * @param notificationIds Array of notification IDs
   * @returns boolean indicating if refresh is needed
   */
  public shouldRefreshHeader(notificationIds: string[]): boolean {
    const headerIds = this.#headerNotificationsSignal().map(n => n.notificationRecipientsId);
    return notificationIds.some(id => headerIds.includes(id));
  }

  public updatePageNotificationReadStatus(notificationId: string): void {
    this.#pageNotificationsSignal.update((responseData) => {
      if (!responseData.data) return responseData;
      
      return {
        ...responseData,
        data: {
          ...responseData.data,
          today: responseData.data.today?.map((notification: Notification) =>
            notificationId === notification.notificationRecipientsId
              ? { ...notification, isRead: true }
              : notification
          ) || [],
          lastWeek: responseData.data.lastWeek?.map((notification: Notification) =>
            notificationId === notification.notificationRecipientsId
              ? { ...notification, isRead: true }
              : notification
          ) || [],
          thisMonth: responseData.data.thisMonth?.map((notification: Notification) =>
            notificationId === notification.notificationRecipientsId
              ? { ...notification, isRead: true }
              : notification
          ) || [],
          lastMonth: responseData.data.lastMonth?.map((notification: Notification) =>
            notificationId === notification.notificationRecipientsId
              ? { ...notification, isRead: true }
              : notification
          ) || []
        }
      };
    });
  }

  /**
   * Add new notification to both header and page
   * Used when receiving notifications via socket
   * @param notification New notification to add
   */
  public addNewNotification(notification: Notification): void {
    this.#pageNotificationsSignal.update(responseData => {
      if (!responseData.data) return responseData;
      
      return {
        ...responseData,
        data: {
          ...responseData.data,
          count: notification.count ? notification.count : responseData.data.count + 1,
          today: [notification, ...(responseData.data.today || [])]
        }
      };
    });

    this.#headerNotificationsSignal.update(notifications => {
      const updated = [notification, ...notifications];
      return updated.slice(0, 5);
    });
  }
}
